import type { ParaLmsHeaderSupplementDto } from '../services/para-assessment-context.service';
import { questionIdFromChallenge } from './lms-assessment-lms.util';

export type ParaScoreButton = 1 | 2 | 3 | 4;

/** Split scorer copy into display lines (one rule per line). */
export function scoringGuideLines(text: string | null | undefined): string[] {
	if (text == null || String(text).trim() === '') {
		return [];
	}
	return String(text)
		.split(/\n+/)
		.map((line) => line.trim())
		.filter((line) => line.length > 0);
}

export type ScoringGuideButtonMap = {
	scoreHints: Partial<Record<ParaScoreButton, string>>;
	nextHint: string | null;
};

/** Map doc lines to score buttons 1–4 and the in-slide NEXT rule. */
export function mapScoringGuideToButtons(text: string | null | undefined): ScoringGuideButtonMap {
	const lines = scoringGuideLines(text);
	const scoreHints: Partial<Record<ParaScoreButton, string>> = {};
	const nextLines: string[] = [];
	let scoreLineIndex = 0;

	for (const line of lines) {
		if (/\bNEXT\b/i.test(line)) {
			nextLines.push(line);
			continue;
		}

		const buttonFromClick = line.match(/[''"]?([1-4])[''"]?\s+button/i);
		const buttonFromLeadingPoint = line.match(/^(\d)\s+point/i);
		const buttonNum = buttonFromClick
			? Number(buttonFromClick[1])
			: buttonFromLeadingPoint
				? Number(buttonFromLeadingPoint[1])
				: null;

		if (buttonNum != null && buttonNum >= 1 && buttonNum <= 4) {
			scoreHints[buttonNum as ParaScoreButton] = line;
			continue;
		}

		scoreLineIndex += 1;
		if (scoreLineIndex <= 4) {
			scoreHints[scoreLineIndex as ParaScoreButton] = line;
		}
	}

	return {
		scoreHints,
		nextHint: nextLines.length ? nextLines.join(' ') : null,
	};
}

/** Score-button hints for the active slide when iframe controls are not detectable yet. */
export function fallbackScoreButtonHints(
	scoreHints: Partial<Record<ParaScoreButton, string>>,
	activeLmsHeaderId: string | null | undefined,
	supplements: ParaLmsHeaderSupplementDto[]
): Array<{ button: ParaScoreButton; text: string }> {
	const slideKey = normalizeLmsHeaderId(activeLmsHeaderId);
	const slideMatch = slideKey.match(/^slide([1-4])$/);
	if (slideMatch) {
		const button = Number(slideMatch[1]) as ParaScoreButton;
		const text = scoreHints[button];
		return text ? [{ button, text }] : [];
	}

	const headerKey = normalizeLmsHeaderId(supplements[0]?.lmsHeaderId);
	if (headerKey === 'scoring' || supplements.length === 1) {
		return ([1, 2, 3, 4] as ParaScoreButton[])
			.filter((button) => scoreHints[button])
			.map((button) => ({ button, text: scoreHints[button]! }));
	}

	const first = scoreHints[1];
	return first ? [{ button: 1, text: first }] : [];
}

/** Score buttons that receive hints on the active Storyline slide. */
export function scoreButtonsForActiveSlide(activeLmsHeaderId: string | null | undefined): ParaScoreButton[] | null {
	const key = normalizeLmsHeaderId(activeLmsHeaderId);
	if (key === 'slide1') {
		return [1];
	}
	if (key === 'slide2') {
		return [2];
	}
	if (key === 'slide3') {
		return [3, 4];
	}
	if (key === 'scoring') {
		return [1, 2, 3, 4];
	}
	return null;
}

export type StoryScoreButtonProbe = {
	button: ParaScoreButton;
	left: number;
	top: number;
	width: number;
	height: number;
	/** From IntersectionObserver when available (1 = fully on screen). */
	intersectionRatio?: number;
};

/**
 * Infer slide1–slide3 from Storyline score-button layout (PARA doc pattern):
 * - slide N shows score button N on the bottom-left
 * - slide 3 also shows button 4 on the bottom-right
 */
export function inferActiveSlideFromScoreButtonProbes(
	probes: StoryScoreButtonProbe[],
	viewWidth: number,
	supplements: ParaLmsHeaderSupplementDto[] = []
): string | null {
	if (!probes.length || viewWidth <= 0) {
		return null;
	}

	const isPassage = supplements.some((s) => normalizeLmsHeaderId(s.lmsHeaderId) === 'scoring');
	if (isPassage) {
		return 'scoring';
	}

	const visible = probes.filter((p) => p.width > 0 && p.height > 0);
	if (!visible.length) {
		return null;
	}

	if (visible.length === 1) {
		const b = visible[0].button;
		return b <= 3 ? `slide${b}` : 'slide3';
	}

	const midX = viewWidth * 0.5;
	const centerX = (p: StoryScoreButtonProbe) => p.left + p.width / 2;
	const leftButtons = visible.filter((p) => centerX(p) < midX);
	const rightButtons = visible.filter((p) => centerX(p) >= midX);

	if (leftButtons.length === 1) {
		const b = leftButtons[0].button;
		if (b >= 1 && b <= 3) {
			return `slide${b}`;
		}
	}

	if (leftButtons.some((p) => p.button === 3) && rightButtons.some((p) => p.button === 4)) {
		return 'slide3';
	}

	if (leftButtons.length > 0) {
		const primary = leftButtons.reduce((best, p) => (p.button > best.button ? p : best));
		if (primary.button <= 3) {
			return `slide${primary.button}`;
		}
	}

	return null;
}

/** Storyline header id wins; else infer from score-button probes in the iframe. */
export function resolveActiveLmsHeaderId(options: {
	activeLmsHeaderId: string | null | undefined;
	scoreButtonProbes: StoryScoreButtonProbe[];
	viewWidth: number;
	supplements: ParaLmsHeaderSupplementDto[];
	visibleScoreButtons: ParaScoreButton[];
}): string | null {
	const fromStoryline = normalizeLmsHeaderId(options.activeLmsHeaderId);
	if (fromStoryline) {
		return fromStoryline;
	}

	const fromLayout = inferActiveSlideFromScoreButtonProbes(
		options.scoreButtonProbes,
		options.viewWidth,
		options.supplements
	);
	if (fromLayout) {
		return fromLayout;
	}

	return inferActiveSlideFromVisibleButtons(options.visibleScoreButtons);
}

/** True when the active LMS slide is slide 1 (or not yet resolved at challenge load). */
export function isFirstSlideOfLmsChallenge(options: {
	activeLmsHeaderId: string | null | undefined;
	scoreButtonProbes: StoryScoreButtonProbe[];
	viewWidth: number;
	supplements: ParaLmsHeaderSupplementDto[];
	visibleScoreButtons: ParaScoreButton[];
}): boolean {
	const fromLayout =
		inferActiveSlideFromScoreButtonProbes(options.scoreButtonProbes, options.viewWidth, options.supplements) ??
		inferActiveSlideFromVisibleButtons(options.visibleScoreButtons);
	if (fromLayout && fromLayout !== 'slide1') {
		return false;
	}

	const fromStoryline = normalizeLmsHeaderId(options.activeLmsHeaderId);
	if (fromStoryline && fromStoryline !== 'slide1') {
		return false;
	}

	return true;
}

/** Infer slide only when a single score button is visible (all four often stay on screen). */
export function inferActiveSlideFromVisibleButtons(visible: ParaScoreButton[]): string | null {
	if (visible.length !== 1) {
		return null;
	}
	const n = visible[0];
	if (n >= 1 && n <= 3) {
		return `slide${n}`;
	}
	return 'slide3';
}

/** Resolve the doc rule line for score button N from slideN (button 4 uses slide3). */
export function scoreHintForButton(
	supplements: ParaLmsHeaderSupplementDto[],
	button: ParaScoreButton
): string | null {
	const sorted = sortLmsHeaderSupplements(supplements);
	const scoringRow = sorted.find((s) => normalizeLmsHeaderId(s.lmsHeaderId) === 'scoring');
	if (scoringRow) {
		return mapScoringGuideToButtons(scoringRow.supplementaryText).scoreHints[button] ?? null;
	}

	if (button === 4) {
		const slide3 = sorted.find((s) => normalizeLmsHeaderId(s.lmsHeaderId) === 'slide3');
		if (slide3) {
			const hint = mapScoringGuideToButtons(slide3.supplementaryText).scoreHints[4];
			if (hint) {
				return hint;
			}
		}
	}

	const slideRow = sorted.find((s) => normalizeLmsHeaderId(s.lmsHeaderId) === `slide${button}`);
	if (slideRow) {
		return mapScoringGuideToButtons(slideRow.supplementaryText).scoreHints[button] ?? null;
	}

	return null;
}

/** DB rule for button N; null when not seeded. */
export function resolveScoreHintText(
	supplements: ParaLmsHeaderSupplementDto[],
	button: ParaScoreButton
): string | null {
	const fromDb = scoreHintForButton(supplements, button);
	return fromDb ? compactScoringHintText(fromDb) : null;
}

/** DB NEXT rule for the active slide; null when not seeded. */
export function resolveNextHintText(
	supplements: ParaLmsHeaderSupplementDto[],
	activeLmsHeaderId: string | null | undefined
): string | null {
	const fromDb = nextHintForActiveSlide(supplements, activeLmsHeaderId);
	return fromDb ? compactScoringHintText(fromDb) : null;
}

/** NEXT rule for the active slide row (slide1/slide2 only in PARA I templates). */
export function nextHintForActiveSlide(
	supplements: ParaLmsHeaderSupplementDto[],
	activeLmsHeaderId: string | null | undefined
): string | null {
	const active = filterLmsHeaderSupplementsForActiveSlide(supplements, activeLmsHeaderId);
	if (!active[0]?.supplementaryText) {
		return null;
	}
	return mapScoringGuideToButtons(active[0].supplementaryText).nextHint;
}

/** Shorter label for anchored hints (scoring condition without click instruction). */
export function compactScoringHintText(text: string): string {
	const trimmed = text.trim();
	const withoutClick = trimmed.replace(/\s*Click on the.*$/i, '').trim();
	return withoutClick || trimmed;
}

function hintPlacementAbove(anchorTop: number, stageHeight: number): boolean {
	return anchorTop < stageHeight * 0.55;
}

/** Format one scorer line for display (trim click-on-button boilerplate on score lines). */
export function formatScoringGuideLine(line: string): string {
	const trimmed = line.trim();
	if (/Click on the\s+[''"]?[1-4][''"]?\s+button/i.test(trimmed)) {
		const shortened = trimmed.replace(/\s*Click on the.*$/i, '').trim();
		return shortened || trimmed;
	}
	return trimmed;
}

const SCORING_GUIDE_POINTS_PATTERN = /(\d+)\s+points?\b(?:[.:])?/gi;
const SCORING_GUIDE_NEXT_BUTTON_PATTERN = /Click the\s+NEXT\s+button/gi;

export type LmsScoringGuideLineSegment =
	| { kind: 'text'; text: string }
	| { kind: 'points'; value: number; label: string }
	| { kind: 'trailingPoints'; text: string; separator: string; value: number; label: string }
	| { kind: 'nextControl' };

function coalesceNextControl(segments: LmsScoringGuideParseSegment[]): LmsScoringGuideLineSegment[] {
	const out: LmsScoringGuideLineSegment[] = [];
	for (let i = 0; i < segments.length; i++) {
		const current = segments[i];
		const next = segments[i + 1];
		const after = segments[i + 2];
		if (
			current.kind === 'text' &&
			current.text === 'Click the ' &&
			next?.kind === 'nextButton' &&
			after?.kind === 'text' &&
			after.text.startsWith(' button')
		) {
			out.push({ kind: 'nextControl' });
			const remainder = after.text.slice(' button'.length);
			if (remainder.length > 0) {
				out.push({ kind: 'text', text: remainder });
			}
			i += 2;
			continue;
		}
		out.push(current.kind === 'nextButton' ? { kind: 'text', text: `Click the ${current.label} button` } : current);
	}
	return out;
}

function coalesceTrailingPoints(segments: LmsScoringGuideLineSegment[]): LmsScoringGuideLineSegment[] {
	if (!segments.length || segments[segments.length - 1].kind !== 'points') {
		return segments;
	}
	const chip = segments[segments.length - 1];
	if (chip.kind !== 'points') {
		return segments;
	}
	const body = segments.slice(0, -1);
	if (body.some((segment) => segment.kind !== 'text')) {
		return segments;
	}

	let text = body
		.filter((segment): segment is Extract<LmsScoringGuideLineSegment, { kind: 'text' }> => segment.kind === 'text')
		.map((segment) => segment.text)
		.join('')
		.replace(/\s+$/, '');
	let separator = ' ';
	const colonMatch = text.match(/^(.*)(:\s*)$/);
	if (colonMatch) {
		text = colonMatch[1];
		separator = colonMatch[2];
	}

	return [{ kind: 'trailingPoints', text, separator, value: chip.value, label: chip.label }];
}

type LmsScoringGuideParseSegment =
	| { kind: 'text'; text: string }
	| { kind: 'points'; value: number; label: string }
	| { kind: 'nextButton'; label: string };

function pushScoringGuideTextSegment(segments: LmsScoringGuideParseSegment[], text: string): void {
	if (text.length > 0) {
		segments.push({ kind: 'text', text });
	}
}

function parseScoringGuideLineSegment(
	line: string,
	pattern: RegExp,
	segmentKind: 'points' | 'nextButton'
): LmsScoringGuideParseSegment[] {
	const segments: LmsScoringGuideParseSegment[] = [];
	let lastIndex = 0;
	let match: RegExpExecArray | null;
	const expression = new RegExp(pattern.source, pattern.flags);

	while ((match = expression.exec(line)) !== null) {
		pushScoringGuideTextSegment(segments, line.slice(lastIndex, match.index));

		if (segmentKind === 'points') {
			const value = Number(match[1]);
			segments.push({
				kind: 'points',
				value,
				label: String(value),
			});
		} else {
			pushScoringGuideTextSegment(segments, 'Click the ');
			segments.push({ kind: 'nextButton', label: 'NEXT' });
			pushScoringGuideTextSegment(segments, ' button');
		}

		lastIndex = match.index + match[0].length;
	}

	pushScoringGuideTextSegment(segments, line.slice(lastIndex));
	return segments;
}

function expandScoringGuideTextSegments(
	segments: LmsScoringGuideParseSegment[],
	pattern: RegExp,
	segmentKind: 'points' | 'nextButton'
): LmsScoringGuideParseSegment[] {
	const expanded: LmsScoringGuideParseSegment[] = [];
	for (const segment of segments) {
		if (segment.kind !== 'text') {
			expanded.push(segment);
			continue;
		}
		expanded.push(...parseScoringGuideLineSegment(segment.text, pattern, segmentKind));
	}
	return expanded;
}

/** True when at least one Storyline score button (1–4) is on-screen and sized for input. */
export function hasOnScreenStoryScoreButtons(
	probes: StoryScoreButtonProbe[],
	visibleScoreButtons: ParaScoreButton[]
): boolean {
	const onScreen = probes.filter((p) => p.width > 0 && p.height > 0);
	if (probes.length > 0) {
		return onScreen.length > 0;
	}
	return visibleScoreButtons.length > 0;
}

/** Split a guide line into plain text, score chips, and Storyline NEXT control groups. */
export function parseScoringGuideLineSegments(line: string): LmsScoringGuideLineSegment[] {
	const withPoints = parseScoringGuideLineSegment(line, SCORING_GUIDE_POINTS_PATTERN, 'points');
	const expanded = expandScoringGuideTextSegments(withPoints, SCORING_GUIDE_NEXT_BUTTON_PATTERN, 'nextButton');
	const segments = expanded.length > 0 ? expanded : [{ kind: 'text' as const, text: line }];
	return coalesceTrailingPoints(coalesceNextControl(segments));
}

/** Full active-slide scoring guide (one line per rule from the Word doc). */
export function buildLmsActiveSlideGuideLines(options: {
	supplements: ParaLmsHeaderSupplementDto[];
	activeLmsHeaderId: string | null | undefined;
	scoreButtonProbes?: StoryScoreButtonProbe[];
	viewWidth?: number;
	visibleScoreButtons: ParaScoreButton[];
	nextControlVisible: boolean;
}): string[] {
	const activeId = resolveActiveLmsHeaderId({
		activeLmsHeaderId: options.activeLmsHeaderId,
		scoreButtonProbes: options.scoreButtonProbes ?? [],
		viewWidth: options.viewWidth ?? 0,
		supplements: options.supplements,
		visibleScoreButtons: options.visibleScoreButtons,
	});

	const probes = options.scoreButtonProbes ?? [];
	const isPassage = options.supplements.some((s) => normalizeLmsHeaderId(s.lmsHeaderId) === 'scoring');
	const hasScoringControls = isPassage || hasOnScreenStoryScoreButtons(probes, options.visibleScoreButtons);
	if (!hasScoringControls || !options.supplements.length || !activeId) {
		return [];
	}

	const active = filterLmsHeaderSupplementsForActiveSlide(options.supplements, activeId);
	const text = active[0]?.supplementaryText;
	if (!text) {
		return [];
	}

	return scoringGuideLines(text).map(formatScoringGuideLine).filter((line) => line.length > 0);
}

export type LmsHeaderScoringHint = {
	kind: 'score' | 'next';
	button?: ParaScoreButton;
	text: string;
};

/**
 * Compact header hints for the active slide — only when score / NEXT controls are visible in the story.
 */
export function buildLmsHeaderScoringHints(options: {
	supplements: ParaLmsHeaderSupplementDto[];
	activeLmsHeaderId: string | null | undefined;
	visibleScoreButtons: ParaScoreButton[];
	nextControlVisible: boolean;
}): LmsHeaderScoringHint[] {
	const isPassage = options.supplements.some((s) => normalizeLmsHeaderId(s.lmsHeaderId) === 'scoring');
	let activeId = options.activeLmsHeaderId;
	if (!activeId && options.visibleScoreButtons.length > 0) {
		activeId = inferActiveSlideFromVisibleButtons(options.visibleScoreButtons);
	}

	const activeButtons: ParaScoreButton[] | null = isPassage
		? [1, 2, 3, 4]
		: scoreButtonsForActiveSlide(activeId);

	const out: LmsHeaderScoringHint[] = [];

	for (const button of options.visibleScoreButtons) {
		if (activeButtons && !activeButtons.includes(button)) {
			continue;
		}
		const text = resolveScoreHintText(options.supplements, button);
		if (!text) {
			continue;
		}
		out.push({ kind: 'score', button, text });
	}

	if (options.nextControlVisible) {
		const text = resolveNextHintText(options.supplements, activeId);
		if (text) {
			out.push({ kind: 'next', text });
		}
	}

	out.sort((a, b) => {
		if (a.kind === 'next') {
			return 1;
		}
		if (b.kind === 'next') {
			return -1;
		}
		return (a.button ?? 0) - (b.button ?? 0);
	});

	return out;
}

export type LmsAnchoredStoryHintRect = {
	centerX: number;
	anchorTop: number;
	anchorBottom: number;
};

export type LmsAnchoredStoryHint = {
	kind: 'score' | 'next';
	button?: ParaScoreButton;
	text: string;
	placementAbove: boolean;
} & LmsAnchoredStoryHintRect;

/** Map a viewport rect into coordinates relative to the story stage container. */
export function stageCoordsFromViewportRect(
	rect: DOMRectReadOnly,
	stageRect: DOMRectReadOnly
): LmsAnchoredStoryHintRect {
	return {
		centerX: rect.left - stageRect.left + rect.width / 2,
		anchorTop: rect.top - stageRect.top,
		anchorBottom: rect.bottom - stageRect.top,
	};
}

/**
 * One hint per visible Storyline control only (score anchors / NEXT anchor must be in view).
 * Button N uses slideN copy from the DB when present.
 */
export function buildLmsAnchoredStoryHints(options: {
	supplements: ParaLmsHeaderSupplementDto[];
	activeLmsHeaderId: string | null | undefined;
	scoreAnchors: Array<{ button: ParaScoreButton } & LmsAnchoredStoryHintRect>;
	nextAnchor: LmsAnchoredStoryHintRect | null;
	stageHeight?: number;
}): LmsAnchoredStoryHint[] {
	const stageHeight = options.stageHeight ?? 600;
	const out: LmsAnchoredStoryHint[] = [];

	for (const anchor of options.scoreAnchors) {
		const text = resolveScoreHintText(options.supplements, anchor.button);
		if (!text) {
			continue;
		}
		out.push({
			kind: 'score',
			button: anchor.button,
			text,
			centerX: anchor.centerX,
			anchorTop: anchor.anchorTop,
			anchorBottom: anchor.anchorBottom,
			placementAbove: hintPlacementAbove(anchor.anchorTop, stageHeight),
		});
	}

	if (options.nextAnchor) {
		const text = resolveNextHintText(options.supplements, options.activeLmsHeaderId);
		if (text) {
			out.push({
				kind: 'next',
				text,
				centerX: options.nextAnchor.centerX,
				anchorTop: options.nextAnchor.anchorTop,
				anchorBottom: options.nextAnchor.anchorBottom,
				placementAbove: hintPlacementAbove(options.nextAnchor.anchorTop, stageHeight),
			});
		}
	}

	return out;
}

/** Build bottom-strip hints; prefers visible iframe controls, falls back to active slide rules. */
export function buildLmsStoryControlHints(options: {
	supplements: ParaLmsHeaderSupplementDto[];
	activeLmsHeaderId: string | null | undefined;
	visibleScoreButtons: ParaScoreButton[];
	nextControlVisible: boolean;
}): {
	scoreButtonHints: Array<{ button: ParaScoreButton; text: string }>;
	nextHintText: string | null;
} {
	const activeSupplements = filterLmsHeaderSupplementsForActiveSlide(
		options.supplements,
		options.activeLmsHeaderId
	);
	const guideText = activeSupplements[0]?.supplementaryText;
	if (!guideText) {
		return { scoreButtonHints: [], nextHintText: null };
	}

	const { scoreHints, nextHint } = mapScoringGuideToButtons(guideText);
	let scoreButtonHints = fallbackScoreButtonHints(scoreHints, options.activeLmsHeaderId, activeSupplements);

	if (options.visibleScoreButtons.length > 0) {
		const visibleMapped = options.visibleScoreButtons
			.map((button) => {
				const text = scoreHints[button];
				return text ? { button, text } : null;
			})
			.filter((row): row is { button: ParaScoreButton; text: string } => row != null);
		if (visibleMapped.length > 0) {
			scoreButtonHints = visibleMapped;
		}
	}

	const nextHintText = options.nextControlVisible ? (nextHint ?? null) : null;

	return { scoreButtonHints, nextHintText };
}

/** Normalize Storyline / DB header keys (e.g. "Slide 1", "slide-1" → "slide1"). */
export function normalizeLmsHeaderId(id: string | null | undefined): string {
	if (id == null) {
		return '';
	}
	const s = String(id).trim().toLowerCase();
	if (!s) {
		return '';
	}
	const slideMatch = s.match(/^slide[\s_-]*(\d+)$/);
	if (slideMatch) {
		return `slide${slideMatch[1]}`;
	}
	if (s === 'scoring') {
		return 'scoring';
	}
	return s.replace(/\s+/g, '');
}

function sortLmsHeaderSupplements(supplements: ParaLmsHeaderSupplementDto[]): ParaLmsHeaderSupplementDto[] {
	return [...supplements].sort((a, b) => {
		const orderDiff = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
		if (orderDiff !== 0) {
			return orderDiff;
		}
		return normalizeLmsHeaderId(a.lmsHeaderId).localeCompare(normalizeLmsHeaderId(b.lmsHeaderId));
	});
}

/**
 * Show one supplement row for the active Storyline slide (`paraLmsSetActiveHeader`).
 * Until Storyline reports a slide: single-row challenges show that row; multi-slide defaults to slide1.
 */
export function filterLmsHeaderSupplementsForActiveSlide(
	supplements: ParaLmsHeaderSupplementDto[],
	activeLmsHeaderId: string | null | undefined
): ParaLmsHeaderSupplementDto[] {
	if (!Array.isArray(supplements) || supplements.length === 0) {
		return [];
	}

	const active = normalizeLmsHeaderId(activeLmsHeaderId);
	if (active) {
		const matched = supplements.filter((s) => normalizeLmsHeaderId(s.lmsHeaderId) === active);
		if (matched.length) {
			return matched;
		}
	}

	if (supplements.length === 1) {
		return supplements;
	}

	const slide1 = supplements.filter((s) => normalizeLmsHeaderId(s.lmsHeaderId) === 'slide1');
	if (slide1.length) {
		return slide1;
	}

	return [sortLmsHeaderSupplements(supplements)[0]];
}

/**
 * Supplementary LMS header copy for the challenge currently loaded in the iframe.
 */
export function resolveLmsHeaderSupplementsForChallenge(
	challenge: any | null | undefined,
	questions: any[],
	assessmentSupplements?: ParaLmsHeaderSupplementDto[] | null
): ParaLmsHeaderSupplementDto[] {
	if (!challenge || typeof challenge !== 'object') {
		return [];
	}

	const onChallenge = (challenge as { lmsHeaderSupplements?: ParaLmsHeaderSupplementDto[] }).lmsHeaderSupplements;
	if (Array.isArray(onChallenge) && onChallenge.length > 0) {
		return sortLmsHeaderSupplements(onChallenge);
	}

	const qid = questionIdFromChallenge(challenge);
	if (qid != null && Array.isArray(questions)) {
		const row = questions.find((q) => Number(q?.id) === qid);
		const onQuestion = row?.lmsHeaderSupplements;
		if (Array.isArray(onQuestion) && onQuestion.length > 0) {
			return sortLmsHeaderSupplements(onQuestion);
		}
	}

	const qn = Number((challenge as { questionNumber?: number }).questionNumber);
	if (Number.isFinite(qn) && qn > 0 && Array.isArray(assessmentSupplements)) {
		const filtered = assessmentSupplements.filter((s) => s.questionNumber === Math.floor(qn));
		return sortLmsHeaderSupplements(filtered);
	}

	return [];
}
